/* @jsxImportSource @emotion/react */
import React from 'react'

import { PlayIcon } from '@slite/icons'

import { brandColorBackground } from '../../utils/grain'
import { fontSize } from './constants'

const boxCss = {
  aspectRatio: '16 / 9',
  borderRadius: 24,
  background: 'var(--shade-10)',
  '@media screen and (max-width: 40rem)': { borderRadius: 16 },
} as const

const FILL_CLASS = 'position-coverer width-full height-full border-none'

// Keeps a logo clear of the card edges at any aspect ratio.
const containedImageCss = { padding: '22% 34%', objectFit: 'contain' } as const

const watchButtonCss = {
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  gap: 10,
  padding: '8px 20px 8px 8px',
  borderRadius: 999,
  background: 'rgba(0, 0, 0, 0.45)',
  backdropFilter: 'blur(10px)',
  fontSize: fontSize.small,
  transition: 'background .2s cubic-bezier(.215,.61,.355,1)',
  '&:hover': { background: 'rgba(0, 0, 0, 0.8)' },
} as const

const playCircleCss = { width: 32, height: 32 } as const

export function ArticleHero(props: {
  imageUrl?: string
  imageAlt?: string
  imageFit?: 'cover' | 'contain'
  backgroundColor?: string
  videoEmbedUrl?: string
  watchButtonLabel?: string
  autoPlayVideo?: boolean
  title: string
}) {
  const [isPlaying, setIsPlaying] = React.useState(false)
  const playerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (props.autoPlayVideo) {
      setIsPlaying(true)
    }
  }, [props.autoPlayVideo])

  // The focused play button unmounts; keep keyboard focus on the player.
  React.useEffect(() => {
    if (isPlaying) {
      playerRef.current?.focus()
    }
  }, [isPlaying])

  if (!props.imageUrl && !props.videoEmbedUrl) {
    return null
  }

  const canPlay = Boolean(props.videoEmbedUrl && props.watchButtonLabel)

  return (
    <div
      className="position-relative overflow-hidden"
      css={[
        boxCss,
        props.backgroundColor && brandColorBackground(props.backgroundColor),
      ]}
    >
      {isPlaying && props.videoEmbedUrl ? (
        <div
          ref={playerRef}
          tabIndex={-1}
          aria-label={props.title}
          className={FILL_CLASS}
        >
          <iframe
            src={props.videoEmbedUrl}
            title={props.watchButtonLabel ?? props.title}
            allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className={FILL_CLASS}
          />
        </div>
      ) : (
        <React.Fragment>
          {props.imageUrl && (
            // Not ImgSanity: it throws on a relative src and re-fetches after hydration.
            <img
              src={props.imageUrl}
              alt={props.imageAlt ?? ''}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className={FILL_CLASS}
              css={
                props.imageFit === 'contain'
                  ? containedImageCss
                  : { objectFit: 'cover' }
              }
            />
          )}
          {canPlay && (
            <button
              type="button"
              onClick={() => setIsPlaying(true)}
              className="position-absolute flexbox-inline flexbox-align-center border-none pointer shade-100 font-weight-semibold"
              css={watchButtonCss}
            >
              <span
                className="flexbox-inline flexbox-centered radius-round background-shade-100 shade-00"
                css={playCircleCss}
                aria-hidden="true"
              >
                <PlayIcon size={12} style={{ marginLeft: 3 }} />
              </span>
              {props.watchButtonLabel}
            </button>
          )}
        </React.Fragment>
      )}
    </div>
  )
}
